import './editor.scss';
import Visibility from '@components/visibility';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import { DEFAULT_CSS_CLASS } from '@constants';
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import classnames from 'classnames';
import {
  setUniqueIdAttribute,
  filterAllowedBlocks,
  flexClasses,
} from '@helpers/utils.js';
import {
  FocusableIcon,
  FocusableIconSwitcher,
} from '@components/focusable-block-icon';
import Label from '@components/block-label';
import BackgroundTypes from '@components/background-types';
import BackgroundTypesSave from '@components/background-types/save';
import FlexSettings from '@components/flex-settings';
import { title } from './block.json';
import { divIcon } from './icon';

const {
  blockEditor: { useBlockProps, useInnerBlocksProps },
  element: { useEffect },
} = wp;

const InspectorGeneral = ({
  label,
  setAttributes,
  blockVisibility,
  flexSettings,
  name,
  blockId,
  backgroundType,
  backgroundGradientOptions,
  backgroundColorOptions,
  backgroundImageOptions,
  backgroundImageAndColorOptions,
  imageAsBackgroundOptions,
  backgroundVideoOptions,
  imageGradientBackgroundOptions,
  blockClassNames,
  printBackgroundStyles,
}) => (
  <InspectorControlsGeneral>
    <Label {...{ label, setAttributes }} />
    <Visibility {...{ blockVisibility, setAttributes }} />

    <FlexSettings {...{ flexSettings, setAttributes, name }} />

    <BackgroundTypes
      {...{
        blockId,
        setAttributes,
        backgroundType,
        backgroundGradientOptions,
        backgroundColorOptions,
        backgroundImageOptions,
        backgroundImageAndColorOptions,
        imageAsBackgroundOptions,
        backgroundVideoOptions,
        imageGradientBackgroundOptions,
        blockClassNames,
        printBackgroundStyles,
      }}
    />
  </InspectorControlsGeneral>
);

const InspectorAppearance = ({
  blockClassNames,
  setAttributes,
  defaultBlockCssClass,
  enableFocusableIcon,
}) => (
  <InspectorControlsAppearance>
    <ClassDropdown
      {...{
        blockClassNames,
        setAttributes,
        initialOpen: true,
        blockClassPrefix: defaultBlockCssClass,
      }}
    />
    <FocusableIconSwitcher
      {...{
        enableFocusableIcon,
        setAttributes,
      }}
    />
  </InspectorControlsAppearance>
);

const Edit = ({ name, context, attributes, setAttributes }) => {
  const { allowedBlocksInsideDiv } = context;
  const {
    blockVisibility,
    blockClassNames,
    blockId,
    backgroundType,
    backgroundGradientOptions,
    backgroundColorOptions,
    backgroundImageOptions,
    backgroundImageAndColorOptions,
    imageAsBackgroundOptions,
    backgroundVideoOptions,
    imageGradientBackgroundOptions,
    flexSettings,
    label,
    enableFocusableIcon,
    lockTemplate,
    isCollapsed,
    printBackgroundStyles,
  } = attributes;
  useEffect(() => {
    setUniqueIdAttribute(name, 'blockId', attributes, setAttributes);
  }, [name, attributes, setAttributes]);

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.DIV;
  const defaultBlockDynamicCssClass = `${defaultBlockCssClass}-${blockId}`;
  const classNames = getBlockClassNames(blockClassNames);
  const flexSettingsClass = flexClasses(flexSettings);

  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}`]: true,
      [`${defaultBlockDynamicCssClass}`]: defaultBlockDynamicCssClass,
      [`${classNames}`]: classNames,
      [`${flexSettingsClass}`]: flexSettingsClass,
      'has-focusable-icon': enableFocusableIcon,
      'is-collapsed': isCollapsed,
      disable: !blockVisibility,
    }),
  });
  const templateLock = lockTemplate === 'false' ? false : lockTemplate;

  const { children, ...innerBlocksProps } = useInnerBlocksProps(blockProps, {
    allowedBlocks: filterAllowedBlocks(allowedBlocksInsideDiv),
    templateLock,
  });

  return (
    <>
      <InspectorTabs>
        <InspectorGeneral
          {...{
            label,
            setAttributes,
            blockVisibility,
            flexSettings,
            name,
            blockId,
            backgroundType,
            backgroundGradientOptions,
            backgroundColorOptions,
            backgroundImageOptions,
            backgroundImageAndColorOptions,
            imageAsBackgroundOptions,
            imageGradientBackgroundOptions,
            backgroundVideoOptions,
            blockClassNames,
            printBackgroundStyles,
          }}
        />
        <InspectorAppearance
          {...{
            blockClassNames,
            setAttributes,
            defaultBlockCssClass,
            enableFocusableIcon,
          }}
        />
      </InspectorTabs>

      <div {...innerBlocksProps}>
        {enableFocusableIcon && (
          <FocusableIcon
            {...{ title, attributes, setAttributes, icon: divIcon }}
          />
        )}
        <BackgroundTypesSave
          {...{
            blockId,
            defaultBlockCssClass,
            backgroundType,
            backgroundGradientOptions,
            backgroundColorOptions,
            backgroundImageOptions,
            backgroundImageAndColorOptions,
            imageAsBackgroundOptions,
            imageGradientBackgroundOptions,
            backgroundVideoOptions,
          }}
        />
        {children}
      </div>
    </>
  );
};

export default Edit;
